import React, { useEffect } from 'react';
import { useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom house marker icon
export const houseMarkerIcon = new L.DivIcon({
  className: 'house-marker',
  html: `<div style="
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border-radius: 3px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    font-size: 10px;
  ">🏠</div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

// Grid label marker
export const createGridLabelIcon = (label) => new L.DivIcon({
  className: 'grid-label-marker',
  html: `<div style="
    padding: 4px 8px;
    background: rgba(34, 197, 94, 0.9);
    color: white;
    border-radius: 4px;
    font-weight: bold;
    font-size: 12px;
    white-space: nowrap;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  ">${label}</div>`,
  iconSize: [40, 20],
  iconAnchor: [20, 10],
});

// Center point icon (for circle zones)
export const centerPointIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="
    width: 20px;
    height: 20px;
    background: #f97316;
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(249, 115, 22, 0.5);
    cursor: grab;
  "></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// Midpoint marker icon (smaller, for adding new points on edges)
export const midpointMarkerIcon = new L.DivIcon({
  className: 'midpoint-marker',
  html: `<div style="
    width: 12px;
    height: 12px;
    background: rgba(147, 51, 234, 0.5);
    border: 2px solid #9333ea;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.2s;
  " onmouseover="this.style.background='#9333ea'; this.style.transform='scale(1.3)';" 
     onmouseout="this.style.background='rgba(147, 51, 234, 0.5)'; this.style.transform='scale(1)';"></div>`,
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

// Map click handler component
export const MapClickHandler = ({ onMapClick, onCircleClick, isDrawing, isCircleMode }) => {
  useMapEvents({
    click(e) {
      if (isCircleMode) {
        onCircleClick(e.latlng);
      } else if (isDrawing) {
        onMapClick(e.latlng);
      }
    },
  });
  return null;
};

// Radius-draw handler — two-click pattern with live mouse-tracking preview.
//   1st click: places the center; the live circle starts following the mouse.
//   mousemove: parent receives a continuous "preview radius in meters" update so it can
//             render the expanding <Circle> + the mile-count tooltip.
//   2nd click: finalizes — parent converts the circle to a polygon and pushes it
//             onto the zones array, then resets back to idle.
// Right-click / Escape cancels the in-progress draw.
export const RadiusDrawHandler = ({ active, center, onSetCenter, onPreviewRadius, onFinalize, onCancel }) => {
  useMapEvents({
    click(e) {
      if (!active) return;
      if (!center) {
        onSetCenter([e.latlng.lat, e.latlng.lng]);
      } else {
        const distMeters = L.latLng(center).distanceTo(e.latlng);
        onFinalize([e.latlng.lat, e.latlng.lng], distMeters);
      }
    },
    mousemove(e) {
      if (!active || !center) return;
      const distMeters = L.latLng(center).distanceTo(e.latlng);
      onPreviewRadius(distMeters, [e.latlng.lat, e.latlng.lng]);
    },
    contextmenu(e) {
      if (!active) return;
      e.originalEvent.preventDefault();
      onCancel();
    },
  });

  // Escape-to-cancel — pressed at the document level so it works even when
  // the map doesn't have focus (e.g. the user just opened the page).
  useEffect(() => {
    if (!active) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [active, onCancel]);

  return null;
};

// Map bounds fitter component
export const MapBoundsFitter = ({ points }) => {
  const map = useMap();
  
  useEffect(() => {
    if (points && points.length >= 3) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [points, map]);
  
  return null;
};

// Circle bounds fitter
export const CircleBoundsFitter = ({ center, radiusMiles }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      const radiusMeters = radiusMiles * 1609.34;
      const bounds = L.latLng(center).toBounds(radiusMeters * 2);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [center, radiusMiles, map]);
  
  return null;
};

// Map center updater for address search
export const MapCenterUpdater = ({ center, zoom = 16 }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, zoom, { animate: true, duration: 0.5 });
    }
  }, [center, zoom, map]);
  
  return null;
};
